import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase, rest } from '@/lib/supabase';
import { queryKeys } from '@/lib/queryClient';
import type { Lead, LeadStatus } from '@/types/database';

interface UseLeadsOptions {
  status?: LeadStatus;
  page?: number;
  limit?: number;
}

export function useLeads(options?: UseLeadsOptions) {
  const { status, page = 1, limit = 20 } = options || {};

  return useQuery({
    queryKey: queryKeys.leads.list({ status, page }),
    queryFn: async () => {
      let query = rest.from<Lead>('leads')
        .select('*')
        .order('created_at', { ascending: false });

      if (status) {
        query = query.eq('status', status);
      }

      const from = (page - 1) * limit;
      const to = from + limit - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;
      if (error) throw error;

      return {
        leads: data as Lead[],
        total: count || 0,
        totalPages: Math.ceil((count || 0) / limit),
        currentPage: page,
      };
    },
  });
}

export function useLead(id: string) {
  return useQuery({
    queryKey: queryKeys.leads.detail(id),
    queryFn: async () => {
      const { data, error } = await rest.from<Lead>('leads')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data as Lead;
    },
    enabled: !!id,
  });
}

export function useCreateLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (lead: Omit<Lead, 'id' | 'created_at' | 'updated_at' | 'status'>) => {
      const { data, error } = await supabase
        .from('leads')
        .insert({ ...lead, status: 'new' } as any)
        .select()
        .single();

      if (error) throw error;
      return data as Lead;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.leads.all });
    },
  });
}

export function useUpdateLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Lead> & { id: string }) => {
      const { data, error } = await supabase
        .from('leads')
        .update(updates as any)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as Lead;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.leads.all });
      if (data?.id) {
        queryClient.invalidateQueries({ queryKey: queryKeys.leads.detail(data.id) });
      }
    },
  });
}

export function useDeleteLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('leads')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.leads.all });
    },
  });
}

export function useLeadStats() {
  return useQuery({
    queryKey: [...queryKeys.leads.all, 'stats'],
    queryFn: async () => {
      const { data, error } = await rest.from<Lead>('leads')
        .select('status');

      if (error) throw error;

      const stats = {
        total: (data || []).length,
        new: (data || []).filter((l) => l.status === 'new').length,
        contacted: (data || []).filter((l) => l.status === 'contacted').length,
        qualified: (data || []).filter((l) => l.status === 'qualified').length,
        proposal: (data || []).filter((l) => l.status === 'proposal').length,
        won: (data || []).filter((l) => l.status === 'won').length,
        lost: (data || []).filter((l) => l.status === 'lost').length,
      };

      return stats;
    },
  });
}
